import { useEffect, useRef } from "react";
import { type FlagBoardProps, isLight, useCellClock } from "./boards";
import { clamp01, css, easeOut, shade } from "./flag";
import { useCanvasLoop } from "./useCanvasLoop";

const STITCH_MS = 170;

/** Snake order, row by row — the way a hand works across the cloth. */
const snake = (w: number) => (fresh: number[]) =>
  [...fresh].sort((a, b) => {
    const ya = Math.floor(a / w);
    const yb = Math.floor(b / w);
    if (ya !== yb) return ya - yb;
    return ya % 2 === 0 ? a - b : b - a;
  });

/**
 * «Вишивка»: the flag as cross-stitch on linen in a wooden hoop. Every revealed
 * cell is one × — the first leg, then the second. Giving up shows the pattern
 * as a flat chart instead: the flag, but never stitched.
 */
export function StitchBoard({ w, h, palette, revealed, status }: FlagBoardProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const cloth = useRef<{ key: string; img: HTMLCanvasElement } | null>(null);
  const { starts, scheduled } = useCellClock(revealed, snake(w), status === "playing" ? 900 : 1300);

  const kick = useCanvasLoop(ref, (ctx, W, H, now) => {
    const light = isLight();
    const cell = W / w;
    // The cloth (colour + holes) is cached per size and theme.
    const key = `${W}x${H}:${light}`;
    if (cloth.current?.key !== key) {
      const img = document.createElement("canvas");
      img.width = W;
      img.height = H;
      const c = img.getContext("2d");
      if (c) {
        c.fillStyle = light ? "#ece6d8" : "#18181a";
        c.fillRect(0, 0, W, H);
        c.fillStyle = light ? "rgba(70,52,30,0.16)" : "rgba(255,255,255,0.08)";
        const r = Math.max(0.6, cell * 0.09);
        for (let y = 0; y <= h; y++) for (let x = 0; x <= w; x++) c.fillRect(x * cell - r, y * cell - r, r * 2, r * 2);
      }
      cloth.current = { key, img };
    }
    ctx.drawImage(cloth.current.img, 0, 0);

    const chart = status === "lost";
    const pad = cell * 0.16;
    ctx.lineCap = "round";
    let busy = false;
    for (let i = 0; i < revealed.length; i++) {
      const ch = revealed[i];
      if (ch === undefined || ch === "-") continue;
      const col = palette[Number(ch)];
      if (!col) continue;
      const start = starts[i] ?? 0;
      const p = start === 0 ? 1 : clamp01((now - start) / STITCH_MS);
      if (p < 1) busy = true;
      if (p <= 0) continue;
      const x = (i % w) * cell;
      const y = Math.floor(i / w) * cell;
      if (chart) {
        ctx.globalAlpha = easeOut(p);
        ctx.fillStyle = css(col);
        ctx.fillRect(x + 0.5, y + 0.5, cell - 1, cell - 1);
        ctx.globalAlpha = 1;
        continue;
      }
      const leg1 = easeOut(clamp01(p * 2));
      const leg2 = easeOut(clamp01(p * 2 - 1));
      ctx.lineWidth = cell * 0.3;
      ctx.strokeStyle = css(shade(col, -0.14));
      ctx.beginPath();
      ctx.moveTo(x + pad, y + pad);
      ctx.lineTo(x + pad + (cell - 2 * pad) * leg1, y + pad + (cell - 2 * pad) * leg1);
      ctx.stroke();
      if (leg2 > 0) {
        ctx.beginPath();
        ctx.moveTo(x + cell - pad, y + pad);
        ctx.lineTo(x + cell - pad - (cell - 2 * pad) * leg2, y + pad + (cell - 2 * pad) * leg2);
        ctx.stroke();
        // thread sheen on the top leg
        ctx.lineWidth = cell * 0.09;
        ctx.strokeStyle = css(shade(col, 0.35), 0.55);
        ctx.stroke();
      }
    }
    if (chart) {
      // the chart's grid
      ctx.strokeStyle = light ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.1)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x <= w; x += 4) {
        ctx.moveTo(x * cell, 0);
        ctx.lineTo(x * cell, H);
      }
      for (let y = 0; y <= h; y += 4) {
        ctx.moveTo(0, y * cell);
        ctx.lineTo(W, y * cell);
      }
      ctx.stroke();
    }
    return busy;
  });

  useEffect(() => {
    kick();
  }, [revealed, status, scheduled, kick]);

  return (
    <div className={`fl-hoop ${status === "won" ? "fl-hoop-won" : ""}`}>
      <span className="fl-hoop-screw" aria-hidden="true" />
      <div className="fl-hoop-cloth">
        <canvas ref={ref} className="block w-full" style={{ aspectRatio: `${w} / ${h}` }} />
      </div>
    </div>
  );
}
